/**
 * Vietnam store notice — modal prompt with no market switch or auto-redirect.
 */
class CountryNoticePopup extends HTMLElement {
  connectedCallback() {
    if (this.dataset.initialized === 'true') return;
    this.dataset.initialized = 'true';

    this.messageEl = this.querySelector('[data-country-message]');
    this.storageKey = this.dataset.storageKey || 'theme:vn-store-notice';
    this.detectedCountry = (this.dataset.detectedCountry || 'VN').toUpperCase();
    this.mode = (this.dataset.mode || 'include').toLowerCase();
    this.showOnlyOnce = this.dataset.showOnlyOnce === 'true';
    this.enabled = this.dataset.enabled === 'true';
    this.forceShow = this.dataset.forceShow === 'true';
    this.previouslyFocusedElement = null;
    this.addedPageLock = false;
    this.onDocumentKeydown = this.onDocumentKeydown.bind(this);

    this.bindEvents();
    document.addEventListener('keydown', this.onDocumentKeydown);

    if (this.isEditorPreview()) return;
    if (!this.enabled) return;
    if (this.showOnlyOnce && this.hasDismissed()) return;

    this.scheduleCheck();
  }

  disconnectedCallback() {
    document.removeEventListener('keydown', this.onDocumentKeydown);
    this.newsletterObserver?.disconnect();
    this.unlockPage();
  }

  isEditorPreview() {
    if (typeof Shopify !== 'undefined' && Shopify.designMode) return true;
    if (document.documentElement.classList.contains('shopify-design-mode')) return true;
    if (window.location.search.includes('preview_theme_id')) return true;
    return false;
  }

  bindEvents() {
    this.querySelectorAll('[data-action="dismiss"]').forEach((el) => {
      el.addEventListener('click', () => this.hide());
    });

    this.querySelector('[data-action="go-vn"]')?.addEventListener('click', () => {
      this.rememberDismissal();
    });
  }

  getFocusableElements() {
    return Array.from(this.querySelectorAll('a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])'))
      .filter((element) => element.getClientRects().length > 0);
  }

  onDocumentKeydown(event) {
    if (!this.classList.contains('is-visible')) return;

    if (event.key === 'Escape') {
      event.preventDefault();
      this.hide();
      return;
    }

    if (event.key !== 'Tab') return;

    const focusableElements = this.getFocusableElements();
    const card = this.querySelector('.country-notice__card');

    if (focusableElements.length === 0) {
      event.preventDefault();
      card?.focus({ preventScroll: true });
      return;
    }

    const firstElement = focusableElements[0];
    const lastElement = focusableElements[focusableElements.length - 1];

    if (!this.contains(document.activeElement)) {
      event.preventDefault();
      firstElement.focus({ preventScroll: true });
    } else if (event.shiftKey && (document.activeElement === firstElement || document.activeElement === card)) {
      event.preventDefault();
      lastElement.focus({ preventScroll: true });
    } else if (!event.shiftKey && document.activeElement === lastElement) {
      event.preventDefault();
      firstElement.focus({ preventScroll: true });
    }
  }

  hasDismissed() {
    try {
      return localStorage.getItem(this.storageKey) === 'true';
    } catch (_error) {
      return false;
    }
  }

  scheduleCheck() {
    const run = () => this.checkGeolocation();

    if ('requestIdleCallback' in window) {
      requestIdleCallback(run, { timeout: 3000 });
    } else {
      window.setTimeout(run, 1500);
    }
  }

  async checkGeolocation() {
    if (!this.enabled || this.hasDismissed()) return;

    const controller = new AbortController();
    const timeoutId = window.setTimeout(() => controller.abort(), 4000);

    try {
      const root = window.Shopify?.routes?.root || '/';
      const response = await fetch(`${root}browsing_context_suggestions.json`, {
        signal: controller.signal,
        credentials: 'same-origin',
        headers: { Accept: 'application/json' }
      });

      if (!response.ok) return;

      const data = await response.json();
      const detected = data?.detected_values?.country?.handle?.toUpperCase();
      const detectedName = data?.detected_values?.country?.name;

      if (!detected) return;
      const inCountry = detected === this.detectedCountry;
      // include: show when IN country (INTL default). exclude: show when NOT in country (VN).
      if (this.mode === 'exclude' ? inCountry : !inCountry) return;

      if (detectedName && this.messageEl) {
        const template = this.messageEl.dataset.template || '';
        this.messageEl.textContent = template.replaceAll('[[country]]', detectedName);
      }

      this.show();
    } catch (_error) {
      /* Skip silently */
    } finally {
      window.clearTimeout(timeoutId);
    }
  }

  show() {
    if (this.classList.contains('is-visible')) return;

    this.previouslyFocusedElement = document.activeElement instanceof HTMLElement
      ? document.activeElement
      : null;
    this.classList.add('is-visible');
    this.setAttribute('aria-hidden', 'false');
    this.lockPage();
    this.holdNewsletterPopup();

    window.requestAnimationFrame(() => {
      this.querySelector('.country-notice__close')?.focus({ preventScroll: true });
    });
  }

  hide() {
    if (!this.classList.contains('is-visible')) return;

    this.classList.remove('is-visible');
    this.setAttribute('aria-hidden', 'true');
    this.unlockPage();
    this.rememberDismissal();
    this.releaseNewsletterPopup();

    if (this.previouslyFocusedElement?.isConnected) {
      this.previouslyFocusedElement.focus({ preventScroll: true });
    }
    this.previouslyFocusedElement = null;
  }

  lockPage() {
    this.addedPageLock = !document.documentElement.classList.contains('lock');
    document.documentElement.classList.add('lock');
  }

  unlockPage() {
    if (!this.addedPageLock) return;

    if (!document.querySelector('[open][aria-modal="true"]')) {
      document.documentElement.classList.remove('lock');
    }
    this.addedPageLock = false;
  }

  getNewsletterPopup() {
    return document.querySelector('newsletter-popup');
  }

  /**
   * Stop the newsletter popup from stacking on top of this region notice.
   * While the notice is visible, any attempt by the newsletter to open is
   * closed and remembered, so it can take the stage once the notice is gone.
   * Only ever runs when the notice actually shows, so a visitor who never
   * sees the notice gets the newsletter's normal, untouched behaviour.
   */
  holdNewsletterPopup() {
    const popup = this.getNewsletterPopup();
    if (!popup) return;

    // Guard against a repeat show() orphaning a live observer.
    if (this.newsletterObserver) this.newsletterObserver.disconnect();
    this.newsletterDeferred = false;

    const suppress = () => {
      if (!this.classList.contains('is-visible') || !popup.hasAttribute('open')) return;
      this.newsletterDeferred = true;
      if (typeof popup.hide === 'function') {
        popup.hide();
      } else {
        popup.removeAttribute('open');
      }
      // The newsletter marks itself "appeared" (theme:popup-appeared) the instant it opens,
      // but we just hid it behind the notice and the shopper never saw it. Un-spend that
      // show-once flag so releaseNewsletterPopup can actually re-show it — otherwise a
      // shopper who is shown the notice loses the popup permanently on every later visit.
      // Only theme:popup-appeared is cleared; theme:popup-filled (subscribed) is preserved.
      try {
        localStorage.removeItem('theme:popup-appeared');
      } catch (_error) {
        /* ignore */
      }
    };

    // Handle the case where the newsletter opened just before the notice.
    suppress();

    this.newsletterObserver = new MutationObserver(suppress);
    this.newsletterObserver.observe(popup, { attributes: true, attributeFilter: ['open'] });
  }

  releaseNewsletterPopup() {
    if (this.newsletterObserver) {
      this.newsletterObserver.disconnect();
      this.newsletterObserver = null;
    }

    if (!this.newsletterDeferred) return;
    this.newsletterDeferred = false;

    const popup = this.getNewsletterPopup();
    if (!popup || typeof popup.show !== 'function') return;

    // Let the notice finish closing before the newsletter takes the stage.
    window.setTimeout(() => popup.show(), 400);
  }

  rememberDismissal() {
    if (!this.showOnlyOnce) return;

    try {
      localStorage.setItem(this.storageKey, 'true');
    } catch (_error) {
      /* ignore */
    }
  }
}

if (!window.customElements.get('country-notice-popup')) {
  window.customElements.define('country-notice-popup', CountryNoticePopup);
}
